<template>
	<div id="top">
		<div class="topbar-container">
			<top-bar
				:phone="defaults[locale].website.phone"
				:email="defaults[locale].website.email"
				:navigation="langmenu"
				:socials="socials"
			/>
		</div>
		<nav class="main-nav fix-on-scroll" :class="{ 'show-mobile-nav': showMobileNav }">
			<div class="row">
				<div class="columns column12">
					<div class="nav-bar fix-on-scroll">
						<nuxt-link class="logo" :to="{ path: localePath('/', locale), hash: '#top' }">
							<img src="~/assets/images/debrink-full.svg" alt="Brasserie Logo" />
						</nuxt-link>
						<div
							v-for="medium in socials"
							:key="medium.ID"
							:class="medium.platform === 'whatsapp' ? 'show' : 'hide'"
						>
							<a
								v-if="medium.platform === 'whatsapp'"
								:href="medium.url"
								:aria-label="medium.platform"
								target="_blank"
							>
								<font-awesome-icon
									v-if="medium.platform === 'whatsapp'"
									:icon="['fab', medium.platform]"
									size="1x"
								/>
							</a>
						</div>
						<div class="nav-content">
							<ul>
								<li v-for="item in topmenu[locale]" :key="item.filename" @click="hideMobileNav"></li>
								<li v-for="item in topmenu[locale]" :key="item.filename" class="nav-item">
									<span @click="hideMobileNav">
										<nuxt-link :to="item.filename">
											{{ item.header }}
										</nuxt-link>
									</span>

									<submenu
										v-if="item.submenu.length"
										:subitems="item.submenu"
										@hide-mobile-nav="hideMobileNav"
									/>
								</li>
								<li class="lang-nav mobile-only">
									<div v-for="item in langmenu" :key="item.filename" class="language">
										<nuxt-link
											class="flag"
											:class="item.language"
											:to="item.filename"
											@click="hideMobileNav"
										>
											<span>{{ item.language }}</span>
										</nuxt-link>
									</div>
								</li>
							</ul>
						</div>
						<div class="buttons">
							<div v-if="!showMobileNav" class="menu-button mobile-only" @click="openMobileNav">
								<font-awesome-icon class="icon" icon="fal fa-bars" size="2x" />
							</div>
							<div v-if="showMobileNav" class="menu-button" @click="hideMobileNav">
								<font-awesome-icon class="icon" icon="fal fa-times" size="2x" />
							</div>

							<book-button class="button book-now cta reserve-now">
								<font-awesome-icon class="calendar-icon" icon="fal fa-calendar-alt" size="1x" />
								<span>{{ $t('bookNow') }}</span>
							</book-button>
						</div>
					</div>
				</div>
			</div>
		</nav>
	</div>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, socials } = useDefaults();

const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();

const showMobileNav = ref(false);

const { data: topmenu } = await useWebsiteFetch('topmenu');

const langmenu = computed(() => {
	return (page.value && page.value.langmenu) ?? [];
});

const openMobileNav = () => {
	showMobileNav.value = true;
};

const hideMobileNav = () => {
	showMobileNav.value = false;
};
</script>

<style lang="scss" scoped>
@media (min-width: 1200px) {
	.nav-item:hover :deep(.submenu ul) {
		display: block;
	}
}

.topbar-container {
	text-align: right;
	padding: 0;
	position: absolute;
	left: 0;
	right: 0;
	top: 0;
	font-size: 13px;
	font-weight: 300;
	height: 40px;
	background: var(--dark-background-color);
}

.columns {
	padding: 0;
}

.mobile-only {
	display: none !important;
}

.show-mobile {
	z-index: 1000;
	position: relative;
}

.main-nav {
	position: fixed;
	top: 40px;
	left: 0;
	right: 0;
	z-index: 100;

	ul li {
		display: inline-block;
	}

	transition: all 0.3s ease-in-out;

	.nav-bar {
		height: 100px;
		transition:
			height 0.3s ease-in-out,
			transform 0.3s ease-in-out;
	}
}

.main-nav::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	border-top: 1px solid rgb(255 255 255 / 50%);
}

.main-nav::after {
	content: '';
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	border-bottom: 1px solid rgb(255 255 255 / 50%);
}

.main-nav.fixed {
	top: 0;
	padding: 0;
	background: var(--dark-background-color);
	box-shadow: 0 0 20px rgb(0 0 0 / 10%);
	transition: all 0.3s ease-in-out;

	.nav-bar {
		height: 100px;
		transition:
			height 0.3s ease-in-out,
			transform 0.3s ease-in-out;
	}
}

.main-nav.fixed::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	border-top: 1px solid rgb(156 31 22 / 100%);
}

.main-nav.fixed::after {
	content: '';
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	border-bottom: 1px solid rgb(156 31 22 / 100%);
}

.nav-bar {
	display: flex;
	align-items: center;
	padding: 0;
	padding-bottom: 0;
	z-index: 9;
	position: relative;

	.buttons {
		display: flex;
		align-items: center;
		gap: 10px;
		position: relative;

		.button {
			padding: 20px 40px;
			margin: 0;
			display: flex;
			justify-content: center;
			align-items: center;
		}

		.button:hover {
			background-color: #98761b;
		}

		.calendar-icon,
		.icon {
			color: #fff;
			display: none;
		}
	}
}

.nav-bar.fixed {
	.buttons {
		padding-top: 0;
	}

	.nav-content {
		padding-top: 0;
	}
}

.logo {
	position: relative;
	z-index: 100;
	padding: 20px 40px;
	text-decoration: none;
	margin-top: -20px;
	background-color: #9c1f16;

	h3 {
		margin: 0;
		font-size: 24px;
	}

	img {
		max-width: 110px;
	}
}

.fix-on-scroll.fixed {
	.logo {
		padding: 10px 60px;
		transform: scale(0.49);
		transform-origin: left center;
		margin: 12px 0;
		box-shadow: 0 0 0 0 rgb(0 0 0 / 0%);
	}
}

.nav-content {
	flex-grow: 1;
	text-align: center;

	a {
		text-decoration: none;
		margin: 0 15px;
		color: #fff;
		letter-spacing: 1px;
		font-size: 14px;
		text-transform: uppercase;
		font-weight: 500;
		position: relative;
		display: inline-block;

		&::after {
			content: '';
			position: absolute;
			left: 50%;
			top: 18px;
			bottom: 0;
			width: 0;
			height: 2px;
			background-color: #fff;
			transition:
				width 0.3s ease-in-out,
				left 0.3s ease-in-out;
		}

		&.router-link-active,
		&:hover {
			color: #fff;

			&::after {
				width: 100%;
				left: 0;
			}
		}
	}

	.menu-close {
		color: #000;
		display: none;
		font-size: 30px;
		cursor: pointer;
		position: absolute;
		top: 15px;
		width: auto;
		right: 20px;
		padding: 0;
		margin: 0;
	}
}

.lang-nav {
	margin: 0 0 25px;

	.language {
		margin: 20px 0;

		.flag::before {
			content: '';
			width: 16px;
			position: relative;
			top: 3px;
			height: 16px;
			margin-right: 6px;
			display: inline-block;
		}

		.flag.en::before {
			background: url('~/assets/images/en.png') no-repeat;
		}

		.flag.nl::before {
			background: url('~/assets/images/nl.png') no-repeat;
		}

		.flag.de::before {
			background: url('~/assets/images/de.png') no-repeat;
		}

		.flag.fr::before {
			background: url('~/assets/images/fr.png') no-repeat;
		}

		.flag.es::before {
			background: url('~/assets/images/es.png') no-repeat;
		}
	}

	img {
		width: 16px;
		float: left;
		margin: 0 5px 0 0;
	}

	a {
		width: 100%;
		float: left;
		color: var(--body-color);
		text-decoration: none;
		margin: 4px 0;
		text-transform: uppercase;

		&.active {
			color: var(--cta-color);
		}
	}
}

.nav-buttons {
	justify-content: space-between;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 22px;
	color: #fff;

	.lang-select {
		border-right: 1px solid var(--alternating-background-color);
	}

	a {
		color: #fff;
		text-decoration: none;
		margin: 0;
		opacity: 0.85;

		&.button,
		&:hover {
			opacity: 1;
		}
	}

	.hide {
		display: none;
	}

	.show {
		display: block !important;
	}
}

.language-navigation:hover .lang-nav-sel,
.language-navigation:hover .fa-globe {
	color: var(--cta-color);
}

.lang-nav-sel {
	box-shadow: none;
	cursor: pointer;
	width: 30px;
	padding: 0 4px 0 0;
	color: #fff;
	border: none;
	text-transform: uppercase;
	font-size: 12px;
	font-family: sans-serif;
	appearance: none;
	background: url('~/assets/images/dropdown.png') right 1px center no-repeat;
	background-size: 7px;
	transition: color 0.3s ease-in-out;

	option {
		color: var(--body-color);
	}

	&::-ms-expand {
		display: none;
	}
}

.lang-select {
	padding: 8px 20px;
	margin: 0 10px;
	border-left: 1px solid #e5e5e5;
	position: relative;
	cursor: pointer;
	text-transform: uppercase;
	font-size: 13px;
	display: flex;
	flex-flow: row nowrap;
	align-items: center;

	span {
		margin: 0 4px;
	}

	a {
		text-decoration: none;
	}

	svg {
		font-size: 12px;
	}
}

.lang-wrapper {
	display: none;
	position: absolute;
	z-index: 999;
	top: 35px;
	left: 3px;
	right: 3px;
	padding: 10px 9px;
	text-align: center;
	background: var(--primary-background-color);
	box-shadow: 0 18px 18px rgb(0 0 0 / 10%);

	&.active,
	a {
		display: flex;
		flex-flow: column nowrap;
		place-content: space-between center;
		color: #000 !important;
		font-size: 13px;
		gap: 5px;
	}

	a {
		width: 100%;
		flex-direction: row;
	}

	img {
		width: 16px;
		height: 16px;
		margin: 2px 0 0;
	}
}

.language-navigation .fa-globe {
	margin: 0 2px 0 0;
	color: var(--body-color);
	transition: color 0.3s ease-in-out;
}

@media (max-width: 1480px) {
	.logo {
		padding: 20px 40px;

		img {
			max-width: 100px;
		}
	}

	.nav-bar a {
		font-size: 12px;
	}

	.nav-bar {
		height: 90px !important;

		.buttons {
			.button {
				padding: 18px 36px;
			}
		}
	}
}

@media (max-width: 1200px) {
	.menu-button {
		position: relative;
		text-transform: uppercase;
		display: flex;
		flex-flow: row nowrap;
		align-items: center;
		justify-content: space-between;
		margin: 0 10px 0 0;
		cursor: pointer;

		svg {
			font-size: 22px;
		}

		span {
			margin: 0 0 0 5px;
		}
	}

	.mobile-only {
		display: flex !important;

		&.lang-nav {
			display: inline-block !important;
			margin: 30px 0 0;
			padding: 0 10px;
		}
	}

	.language-navigation {
		display: none;
	}

	.nav-bar .buttons .icon {
		display: unset;
	}

	.nav-buttons {
		display: none;
	}

	.logo {
		padding: 29px 40px;
		transform: scale(0.5);
		transform-origin: left center;
		margin: 12px 0;
		box-shadow: 0 0 0 0 rgb(0 0 0 / 0%);
		z-index: -10;
	}

	.main-nav {
		position: fixed !important;
		top: 0;
		left: 0;
		right: 0;
		overflow: hidden;

		.main-nav.fixed {
			box-shadow: 0 0 20px rgb(0 0 0 / 10%);
		}

		&.show-mobile-nav {
			inset: 0;

			.nav-content {
				a {
					&::after {
						display: none;
					}
				}
			}

			.nav-content ul {
				display: flex;
				flex-direction: column;
				justify-content: center;
				opacity: 1;
				visibility: visible;
				transition: opacity 0.3s ease-in-out;
				background: var(--dark-background-color);

				li:not(.menu-close) {
					width: 100%;
				}

				li:not(.lang-nav, .menu-close) {
					margin: 7px 0;

					a {
						font-size: 24px;
						font-family: var(--heading-font-family);
						text-transform: unset;
						font-weight: 400;
						line-height: 36px;

						&::after {
							display: none;
						}
					}
				}
			}
		}

		ul {
			position: fixed;
			inset: 0;
			background: var(--primary-background-color);
			text-align: left;
			padding: 20px;
			opacity: 0;
			visibility: hidden;
			overflow-y: scroll;
		}
	}

	.nav-content .menu-close {
		display: block;
	}

	.logo fix-on-scroll {
		h3 {
			font-size: 22px;
		}
	}
}

@media (max-width: 880px) {
	.main-nav {
		top: 0;
		padding: 0;
		box-shadow: 0 0 20px rgb(0 0 0 / 10%);
		transition: all 0.3s ease-in-out;

		.nav-bar {
			height: 100px;
			transition:
				height 0.3s ease-in-out,
				transform 0.3s ease-in-out;
		}
	}

	.nav-bar .buttons .cta .icon {
		display: unset;
	}

	.main-nav::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
	}

	.main-nav::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
	}

	.nav-bar {
		.buttons {
			padding-top: 0 !important;

			.button {
				padding: 14px 16px 17px !important;
			}
		}

		.nav-content {
			padding-top: 0 !important;
		}

		.menu-button {
			margin-right: 5px;

			.icon {
				font-size: 22px;
			}
		}

		.icon {
			margin-right: 0;
			font-size: 16px;
		}

		// span {
		// 	display: none;
		// }
	}

	.fix-on-scroll.fixed {
		.logo {
			padding: 20px 30px;
		}
	}

	.logo {
		padding: 29px 40px;
		transform: scale(0.5);
		transform-origin: left center;
		margin: 12px 0;
		box-shadow: 0 0 0 0 rgb(0 0 0 / 0%);
	}
}

@media (max-width: 580px) {
	.nav-bar .buttons {
		.calendar-icon {
			display: block;
			transform: scale(130%);
		}

		span {
			display: none;
		}

		.nav-bar .logo fix-on-scroll {
			font-size: 22px;
		}
	}
}
</style>
